import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { SenoIcon } from '../components/SenoIcon';
import { ErrorNotice } from '../components/Feedback';
import { get, post, remove } from '../lib/api';
import type { Project } from '../types';

export function OverviewPage({
  project,
  navigate
}: {
  project: Project;
  navigate: (path: string) => void;
}) {
  const base = `/app/project/${project.id}`;
  const apiBase = `/api/projects/${project.id}`;
  type CommercialSummary = {
    entitlement: { planCode: string; status: string; validUntil: string | null };
    identity: { status?: string; reason_json?: string } | null;
    fairUse: { status: 'normal' | 'near_limit' };
  };
  const [commercial, setCommercial] = useState<CommercialSummary | null>(null);
  const [grants, setGrants] = useState<
    Array<{ id: string; scope: string; status: string; expires_at: string }>
  >([]);
  const [error, setError] = useState('');

  const loadCommercial = useCallback(async () => {
    try {
      const [status, support] = await Promise.all([
        get<CommercialSummary>(`${apiBase}/commercial`),
        get<{ grants: typeof grants }>(`${apiBase}/support-grants`)
      ]);
      if (status?.entitlement && status.fairUse) setCommercial(status);
      if (Array.isArray(support?.grants)) setGrants(support.grants);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Status proyek belum dapat dimuat.');
    }
  }, [apiBase]);

  useEffect(() => {
    void loadCommercial();
  }, [loadCommercial]);

  async function createGrant(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      await post(`${apiBase}/support-grants`, {
        adminUserId: form.get('adminUserId'),
        scope: form.get('scope'),
        reason: form.get('reason'),
        expiresInHours: Number(form.get('expiresInHours'))
      });
      formElement.reset();
      await loadCommercial();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Akses support belum dibuat.');
    }
  }
  return (
    <div className="content-page overview-page">
      <div className="hero-welcome">
        <span className="eyebrow">
          {project.type === 'thesis' ? 'Proyek skripsi' : 'Proyek artikel jurnal'}
        </span>
        <h1>{project.name}</h1>
        <p>
          Fondasi dokumen telah dibuat. Lengkapi konteks penelitian, aktifkan pedoman, lalu tulis
          per bagian.
        </p>
        <button className="button button-primary" onClick={() => navigate(`${base}/brain`)}>
          Lengkapi Project Brain →
        </button>
      </div>
      <div className="progress-line">
        <div className="active">
          <span>1</span>
          <strong>Project Brain</strong>
        </div>
        <div>
          <span>2</span>
          <strong>Pedoman</strong>
        </div>
        <div>
          <span>3</span>
          <strong>Dokumen</strong>
        </div>
        <div>
          <span>4</span>
          <strong>Finalisasi</strong>
        </div>
      </div>
      {error ? <ErrorNotice message={error} /> : null}
      {commercial ? (
        <section className="commercial-strip">
          <SenoIcon name="license" />
          <div>
            <strong>Paket {commercial.entitlement.planCode}</strong>
            <p>
              {commercial.entitlement.validUntil
                ? `Aktif sampai ${new Date(commercial.entitlement.validUntil).toLocaleDateString('id-ID')}`
                : 'Demo tanpa masa aktif berulang'}
              {' · '}
              {commercial.fairUse.status === 'near_limit'
                ? 'Pemakaian wajar mendekati batas; tugas berat dapat dibatasi.'
                : 'Pemakaian wajar dalam kondisi normal.'}
            </p>
          </div>
          <span
            className={`badge ${commercial.identity?.status === 'identity_review_required' ? 'badge-warning' : 'badge-active'}`}
          >
            {commercial.identity?.status === 'identity_review_required'
              ? 'Periksa identitas'
              : 'Identitas stabil'}
          </span>
        </section>
      ) : null}
      <section className="overview-actions">
        <button onClick={() => navigate(`${base}/brain`)}>
          <SenoIcon name="skripsi" />
          <div>
            <strong>Project Brain</strong>
            <span>Sumber fakta dan identitas penelitian</span>
          </div>
          <b>→</b>
        </button>
        <button onClick={() => navigate(`${base}/files`)}>
          <SenoIcon name="pedoman" />
          <div>
            <strong>Pedoman aktif</strong>
            <span>Upload dan konfirmasi aturan kampus</span>
          </div>
          <b>→</b>
        </button>
        <button onClick={() => navigate(`${base}/document`)}>
          <SenoIcon name="revisi" />
          <div>
            <strong>Ruang penulisan</strong>
            <span>Chapter, section, content block, autosave</span>
          </div>
          <b>→</b>
        </button>
      </section>
      <section className="integrity-strip">
        <SenoIcon name="konsistensi" />
        <div>
          <strong>Integrity graph sudah disiapkan</strong>
          <p>Rumusan Masalah → Tujuan → Hipotesis → Metode → Evidence → Kesimpulan.</p>
        </div>
        <span className="badge badge-foundation">Foundation</span>
      </section>
      <section className="support-access">
        <div className="section-title">
          <div>
            <span className="eyebrow">Akses support privat</span>
            <h2>Izinkan hanya saat diperlukan</h2>
            <p>
              Akses dibatasi ke akun admin tertentu, scope tertentu, memiliki expiry, dan diaudit.
            </p>
          </div>
        </div>
        <form onSubmit={createGrant} className="support-grant-form">
          <label>
            ID akun support
            <input name="adminUserId" required placeholder="UUID dari petugas support" />
          </label>
          <label>
            Scope
            <select name="scope" defaultValue="diagnostic">
              <option value="diagnostic">Diagnostik</option>
              <option value="document_read">Baca dokumen</option>
              <option value="file_read">Baca file</option>
              <option value="metadata">Metadata</option>
            </select>
          </label>
          <label>
            Durasi
            <select name="expiresInHours" defaultValue="24">
              <option value="4">4 jam</option>
              <option value="24">24 jam</option>
              <option value="72">72 jam</option>
            </select>
          </label>
          <label className="support-reason">
            Alasan
            <input
              name="reason"
              required
              minLength={8}
              placeholder="Contoh: bantu diagnosis export DOCX"
            />
          </label>
          <button className="button button-secondary">Berikan akses sementara</button>
        </form>
        {grants
          .filter((grant) => grant.status === 'active')
          .map((grant) => (
            <div className="support-grant-row" key={grant.id}>
              <span>
                {grant.scope} · sampai {new Date(grant.expires_at).toLocaleString('id-ID')}
              </span>
              <button
                className="button button-ghost"
                onClick={async () => {
                  await remove(`${apiBase}/support-grants/${grant.id}`);
                  await loadCommercial();
                }}
              >
                Cabut
              </button>
            </div>
          ))}
      </section>
    </div>
  );
}
